/**
 * Search (Ctrl+K).
 *
 * One box that finds anything on Pugo: people, parties, crews, games, the
 * pages of the app and the things it can do. It used to only look at what the
 * client happened to have in memory - your friends and the parties you were
 * already in - so searching for a player you had not met found nothing, which
 * made the whole box feel broken. It now asks the server for people, parties
 * and crews while you type, and merges those with the local commands.
 *
 * How it ranks: an exact name first, then a name that starts with what you
 * typed, then a word inside it starting with it, then a plain substring, then
 * the letters in order somewhere in the name. Within a tier the natural order
 * of the group wins. Subsequence matching is last rather than first because
 * "stg" finding Settings is a party trick; "sett" finding it is the job.
 *
 * Everything is keyboard-first: arrows move, Enter opens, Tab walks the
 * filters, Escape closes. Results never jump under the cursor - a slower
 * remote answer is merged in at the end of its group, and the highlighted row
 * is kept by id, not by index.
 */

import { useEffect, useMemo, useRef, useState, type ComponentType, type KeyboardEvent, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, endpoints, socket } from '@/lib/api';
import type { Party, PublicUser } from '@/lib/types';
import { cn } from '@/lib/cn';
import { go } from '@/lib/hash';
import { openFinder } from '@/lib/finder';
import { play } from '@/lib/sound';
import { useSession } from '@/stores/session';
import { useVoice } from '@/stores/voice';
import * as Icons from '@/icons';
import { Avatar } from './Avatar';
import { GameMark } from './GameMark';
import { openCrew, type Crew } from './Crew';
import { CrewMark } from './CrewArt';
import { Kbd } from './ui';

type Scope = 'all' | 'people' | 'parties' | 'crews' | 'app';

type Item = {
  id: string;
  title: string;
  sub?: string;
  group: string;
  scope: Exclude<Scope, 'all'>;
  /** Drawn on the left. A picture wins over a glyph where there is one. */
  icon?: ComponentType<{ size?: number; className?: string }>;
  art?: ReactNode;
  /** Tints the icon plate - used by themes and games to show their colour. */
  color?: string;
  keys?: string;
  danger?: boolean;
  /** Ranked above everything else it ties with. */
  weight?: number;
  run: () => void | Promise<void>;
};

const SCOPES: { id: Scope; label: string }[] = [
  { id: 'all', label: 'Everything' },
  { id: 'people', label: 'People' },
  { id: 'parties', label: 'Parties' },
  { id: 'crews', label: 'Crews' },
  { id: 'app', label: 'App' }
];

const STATUSES = [
  ['online', 'Online'],
  ['idle', 'Away'],
  ['dnd', 'Do not disturb'],
  ['invisible', 'Invisible']
] as const;

const RECENT_KEY = 'pogu.search.recent';
const MAX_RECENT = 6;

function readRecent(): string[] {
  try {
    const raw = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
    return Array.isArray(raw) ? raw.filter((x) => typeof x === 'string').slice(0, MAX_RECENT) : [];
  } catch { return []; }
}

function rememberRecent(term: string) {
  const q = term.trim();
  if (q.length < 2) return;
  try {
    const next = [q, ...readRecent().filter((x) => x.toLowerCase() !== q.toLowerCase())].slice(0, MAX_RECENT);
    localStorage.setItem(RECENT_KEY, JSON.stringify(next));
  } catch { /* private window, or storage full - searching still works */ }
}

/**
 * How well `text` answers `query`, or 0 for not at all.
 *
 * The tiers are far enough apart that no amount of one kind of match ever
 * outranks a better kind, which is what keeps the order stable as remote
 * results arrive.
 */
function score(text: string, query: string): number {
  if (!query) return 1;
  const hay = text.toLowerCase();
  const q = query.toLowerCase();

  if (hay === q) return 1000;
  if (hay.startsWith(q)) return 800 - Math.min(hay.length - q.length, 99);

  /* The start of any word in it: "rank" finds "Rank Tracker" and "My rank". */
  const word = hay.search(new RegExp('\\b' + q.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')));
  if (word >= 0) return 600 - Math.min(word, 99);

  const at = hay.indexOf(q);
  if (at >= 0) return 400 - Math.min(at, 99);

  /* Every letter, in order, somewhere. Last resort. */
  let cursor = 0;
  for (const ch of q) {
    cursor = hay.indexOf(ch, cursor);
    if (cursor === -1) return 0;
    cursor += 1;
  }
  return 120;
}

/** The best score across a row's name and its second line. */
function rank(item: Item, query: string): number {
  if (!query) return (item.weight ?? 0) + 1;
  const title = score(item.title, query);
  const sub = item.sub ? score(item.sub, query) * 0.6 : 0;
  const best = Math.max(title, sub);
  return best ? best + (item.weight ?? 0) : 0;
}

export function Palette({ onClose }: { onClose: () => void }) {
  const [typed, setTyped] = useState('');
  const [term, setTerm] = useState('');
  const [scope, setScope] = useState<Scope>('all');
  const [activeId, setActiveId] = useState('');
  const listRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const me = useSession((s) => s.me);
  const catalog = useSession((s) => s.catalog);
  const saveSettings = useSession((s) => s.saveSettings);
  const signOut = useSession((s) => s.signOut);
  const voice = useVoice();
  const [recent, setRecent] = useState<string[]>(() => readRecent());

  /* What is typed drives the list immediately; what is searched for on the
     server waits a moment, so a five letter name is one request, not five. */
  useEffect(() => {
    const id = window.setTimeout(() => setTerm(typed.trim()), 180);
    return () => window.clearTimeout(id);
  }, [typed]);

  const remote = term.length >= 2;

  const friends = useQuery({ queryKey: ['friends'], queryFn: endpoints.friends, enabled: !!me });
  const myParties = useQuery({
    queryKey: ['parties', 'mine'],
    queryFn: () => endpoints.parties({ scope: 'mine' }),
    enabled: !!me
  });

  const people = useQuery({
    queryKey: ['search', 'players', term],
    queryFn: () => api.get<{ players: PublicUser[] }>('/api/players?q=' + encodeURIComponent(term)),
    enabled: remote && (scope === 'all' || scope === 'people'),
    staleTime: 20_000
  });
  const parties = useQuery({
    queryKey: ['search', 'parties', term],
    queryFn: () => api.get<{ parties: Party[] }>('/api/parties?q=' + encodeURIComponent(term)),
    enabled: remote && (scope === 'all' || scope === 'parties'),
    staleTime: 15_000
  });
  const crews = useQuery({
    queryKey: ['search', 'crews', term],
    queryFn: () => api.get<{ crews: Crew[] }>('/api/crews?q=' + encodeURIComponent(term)),
    enabled: remote && (scope === 'all' || scope === 'crews'),
    staleTime: 30_000
  });

  const searching = people.isFetching || parties.isFetching || crews.isFetching;

  /* ---------------------------------------------------------- the items */

  const items = useMemo<Item[]>(() => {
    const out: Item[] = [];
    const seenPeople = new Set<string>();

    /* People: your friends first, because a friend called "sam" is the sam
       you meant, then everyone the directory found. */
    for (const row of friends.data?.friends ?? []) {
      seenPeople.add(row.user.id);
      const up = row.user.status && row.user.status !== 'offline';
      out.push({
        id: 'friend-' + row.user.id,
        title: row.user.displayName,
        sub: '@' + row.user.username + (up ? ' · online' : '') + ' · friend',
        group: 'Friends',
        scope: 'people',
        art: <Avatar user={row.user} size={30} presence />,
        weight: 60,
        run: () => go('u/' + row.user.username)
      });
    }
    for (const user of people.data?.players ?? []) {
      if (seenPeople.has(user.id) || user.id === me?.id) continue;
      out.push({
        id: 'player-' + user.id,
        title: user.displayName,
        sub: '@' + user.username + (user.status && user.status !== 'offline' ? ' · online' : ''),
        group: 'People',
        scope: 'people',
        art: <Avatar user={user} size={30} presence />,
        weight: 20,
        run: () => go('u/' + user.username)
      });
    }

    /* Parties you are in, then everything open that matches. */
    const mine = new Set<string>();
    for (const party of myParties.data?.parties ?? []) {
      if (party.closed) continue;
      mine.add(party.id);
      const game = catalog?.games.find((g) => g.id === party.gameId);
      out.push({
        id: 'myparty-' + party.id,
        title: party.title,
        sub: 'Your party · ' + (game?.name ?? 'Game') + ' · ' + party.memberCount + '/' + party.size,
        group: 'Your parties',
        scope: 'parties',
        art: <GameMark id={party.gameId} size={30} plain />,
        color: game?.color,
        weight: 70,
        run: () => go('party/' + party.id)
      });
    }
    for (const party of parties.data?.parties ?? []) {
      if (mine.has(party.id) || party.closed) continue;
      const game = catalog?.games.find((g) => g.id === party.gameId);
      out.push({
        id: 'party-' + party.id,
        title: party.title,
        sub: (game?.name ?? 'Game') + ' · ' + party.memberCount + '/' + party.size + ' players'
          + (party.memberCount < party.size ? ' · has room' : ' · full'),
        group: 'Parties',
        scope: 'parties',
        art: <GameMark id={party.gameId} size={30} plain />,
        color: game?.color,
        weight: 15,
        run: () => go('party/' + party.id)
      });
    }

    for (const crew of crews.data?.crews ?? []) {
      out.push({
        id: 'crew-' + crew.id,
        title: crew.name,
        sub: crew.tag + ' · ' + crew.memberCount + (crew.memberCount === 1 ? ' member' : ' members'),
        group: 'Crews',
        scope: 'crews',
        art: <CrewMark tag={crew.tag} appearance={crew.appearance} size={30} className="rounded-[9px]" />,
        weight: 15,
        run: () => openCrew(crew.tag)
      });
    }

    /* Games: a name goes to the parties for it, which is what people want
       when they type "valorant" into a search box on a party app. */
    for (const game of catalog?.games ?? []) {
      out.push({
        id: 'game-' + game.id,
        title: game.name,
        sub: 'Parties for ' + game.short,
        group: 'Games',
        scope: 'parties',
        art: <GameMark id={game.id} size={30} plain />,
        color: game.color,
        weight: 10,
        run: () => go('parties?game=' + game.id)
      });
    }

    /* Pages. */
    const page = (id: string, title: string, sub: string, icon: ComponentType<{ size?: number; className?: string }>, keys?: string) =>
      /* With nothing typed this is a jump list, so the pages come before
         the people - hence a heavier weight than a friend. */
      out.push({ id: 'nav-' + id, title, sub, group: 'Go to', scope: 'app', icon, keys, weight: 65, run: () => go(id) });

    page('home', 'Home', 'What is happening now', Icons.Grid, 'G H');
    page('parties', 'Parties', 'Browse open parties', Icons.Grid, 'G P');
    page('friends', 'Friends', 'Your friends list', Icons.Users, 'G F');
    page('chat', 'Messages', 'Direct messages', Icons.Send, 'G M');
    page('games', 'Games', 'Every game on Pugo', Icons.Grid);
    page('players', 'Players', 'Everyone on Pugo', Icons.Profile);
    page('badges', 'Badges', 'Your collection', Icons.Star);
    page('leaderboard', 'Leaderboards', 'Top of the community', Icons.Chart);
    page('crews', 'Crew', 'Your crew and the directory', Icons.Shield);
    page('notifications', 'Notifications', 'Everything you missed', Icons.Bell);
    page('settings', 'Settings', 'Appearance, voice, privacy', Icons.Palette, 'G S');

    if (me) {
      out.push({
        id: 'nav-me', title: 'My profile', sub: '@' + me.username, group: 'Go to', scope: 'app',
        icon: Icons.Profile, weight: 50, run: () => go('u/' + me.username)
      });
    }
    if (me && me.role === 'admin') {
      out.push({
        id: 'nav-staff', title: 'Staff console', sub: 'Reports, accounts and permissions', group: 'Go to', scope: 'app',
        icon: Icons.Shield, weight: 40, run: () => go('staff')
      });
      /* Searchable, because a page with one button into it is a page you
         forget exists - and this is the one you want at the worst moment. */
      out.push({
        id: 'nav-status', title: 'Server status', sub: 'Load, memory, disk and the switches', group: 'Go to', scope: 'app',
        icon: Icons.Bolt, weight: 40, run: () => go('status')
      });
    }

    /* Things to do. */
    out.push({
      id: 'act-create', title: 'Create a party', sub: 'Say what you want to play', group: 'Actions', scope: 'app',
      icon: Icons.Plus, keys: 'Ctrl N', weight: 45, run: () => go('parties?create=1')
    });
    out.push({
      id: 'act-addfriend', title: 'Add a friend', sub: 'Search for anyone by name', group: 'Actions', scope: 'app',
      icon: Icons.Users, weight: 30, run: () => go('friends?add=1')
    });
    out.push({
      id: 'act-findmsg', title: 'Find a message', sub: 'Search everything you can read', group: 'Actions', scope: 'app',
      icon: Icons.Search, weight: 28, run: () => openFinder({ start: 'search' })
    });
    out.push({
      id: 'act-saved', title: 'Saved messages', sub: 'What you kept for later', group: 'Actions', scope: 'app',
      icon: Icons.Star, weight: 28, run: () => openFinder({ start: 'saved' })
    });

    if (voice.partyId) {
      out.push(
        { id: 'act-mute', title: voice.muted ? 'Unmute microphone' : 'Mute microphone', group: 'Voice', scope: 'app', icon: Icons.Mic, weight: 35, run: () => voice.toggleMute() },
        { id: 'act-deafen', title: voice.deafened ? 'Undeafen' : 'Deafen', group: 'Voice', scope: 'app', icon: Icons.Headset, weight: 35, run: () => voice.toggleDeafen() },
        { id: 'act-leave', title: 'Leave voice', group: 'Voice', scope: 'app', icon: Icons.Close, danger: true, weight: 35, run: () => void voice.leave() }
      );
    }

    for (const [id, label] of STATUSES) {
      out.push({
        id: 'status-' + id,
        title: 'Set status: ' + label,
        sub: 'Everywhere you appear',
        group: 'Status',
        scope: 'app',
        icon: id === 'dnd' ? Icons.Ban : id === 'invisible' ? Icons.Lock : Icons.CheckCircle,
        run: async () => {
          socket.send('status', { status: id });
          await api.patch('/api/me', { status: id }).catch(() => {});
        }
      });
    }

    for (const theme of catalog?.themes ?? []) {
      out.push({
        id: 'theme-' + theme.id,
        title: 'Theme: ' + theme.name,
        sub: theme.group,
        group: 'Themes',
        scope: 'app',
        icon: Icons.Palette,
        color: theme.accent,
        run: () => void saveSettings({ theme: theme.id })
      });
    }

    out.push({
      id: 'act-signout', title: 'Sign out', group: 'Account', scope: 'app',
      icon: Icons.Key, danger: true, run: async () => {
        if (voice.partyId) await voice.leave();
        signOut();
      }
    });

    return out;
  }, [me, catalog, friends.data, myParties.data, people.data, parties.data, crews.data, voice, saveSettings, signOut]);

  /* ---------------------------------------------------------- the list */

  const shown = useMemo(() => {
    const q = typed.trim();
    const pool = scope === 'all' ? items : items.filter((i) => i.scope === scope);
    const scored = pool
      .map((item) => ({ item, s: rank(item, q) }))
      .filter((row) => row.s > 0);

    scored.sort((a, b) => b.s - a.s);

    /* With nothing typed this is a short menu, not a dump of the app. */
    return scored.slice(0, q ? 40 : 12).map((row) => row.item);
  }, [items, typed, scope]);

  /* The cursor follows the row, not its position, so a late remote answer
     cannot move what Enter would open. */
  const cursor = Math.max(0, shown.findIndex((i) => i.id === activeId));
  useEffect(() => {
    if (!shown.length) return;
    if (!shown.some((i) => i.id === activeId)) setActiveId(shown[0]!.id);
  }, [shown, activeId]);

  useEffect(() => {
    listRef.current?.querySelector('[data-active="1"]')?.scrollIntoView({ block: 'nearest' });
  }, [activeId]);

  function move(by: number) {
    if (!shown.length) return;
    const next = shown[(cursor + by + shown.length) % shown.length]!;
    setActiveId(next.id);
  }

  function pick(item: Item | undefined) {
    if (!item) return;
    rememberRecent(typed);
    onClose();
    play('pop');
    void item.run();
  }

  function onKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowDown' || (e.key === 'n' && e.ctrlKey)) { e.preventDefault(); move(1); }
    else if (e.key === 'ArrowUp' || (e.key === 'p' && e.ctrlKey)) { e.preventDefault(); move(-1); }
    else if (e.key === 'PageDown') { e.preventDefault(); move(5); }
    else if (e.key === 'PageUp') { e.preventDefault(); move(-5); }
    else if (e.key === 'Enter') { e.preventDefault(); pick(shown[cursor]); }
    else if (e.key === 'Tab') {
      e.preventDefault();
      const i = SCOPES.findIndex((s) => s.id === scope);
      setScope(SCOPES[(i + (e.shiftKey ? SCOPES.length - 1 : 1)) % SCOPES.length]!.id);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      if (typed) { setTyped(''); setTerm(''); return; }
      onClose();
    }
  }

  const empty = !shown.length;
  let lastGroup = '';

  return (
    <div
      className="soft-in fixed inset-0 z-[460] flex justify-center bg-[rgba(0,0,0,.5)] pt-[11vh] backdrop-blur-[3px]"
      onClick={onClose}
      role="presentation"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Search"
        className={cn(
          'flex h-fit max-h-[74vh] w-[min(680px,94vw)] flex-col overflow-hidden rounded-[var(--radius-l)]',
          'border border-line bg-surface-1 shadow-[var(--shadow-3)] animate-[rise_200ms_var(--ease-out-soft)]'
        )}
        onClick={(e) => e.stopPropagation()}
      >
        {/* ---- the box ---- */}
        <div className="flex flex-none items-center gap-3 px-4 pt-3.5">
          <Icons.Search size={18} className={cn('flex-none transition-colors', typed ? 'text-accent' : 'text-mute')} />
          <input
            ref={inputRef}
            autoFocus
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            onKeyDown={onKeyDown}
            spellCheck={false}
            autoComplete="off"
            aria-label="Search Pugo"
            placeholder="Search people, parties, crews, games…"
            className="h-[42px] min-w-0 flex-1 bg-transparent text-[15.5px] outline-none placeholder:text-ghost"
          />
          {searching ? (
            <span aria-hidden className="h-4 w-4 flex-none animate-spin rounded-full border-2 border-line border-t-accent" />
          ) : typed ? (
            <button
              type="button"
              onClick={() => { setTyped(''); setTerm(''); inputRef.current?.focus(); }}
              aria-label="Clear"
              className="grid h-7 w-7 flex-none place-items-center rounded-full text-mute transition-colors hover:bg-surface-3 hover:text-ink"
            >
              <Icons.Close size={13} />
            </button>
          ) : <Kbd>esc</Kbd>}
        </div>

        {/* ---- what to look in ---- */}
        <div className="no-scrollbar flex flex-none items-center gap-1.5 overflow-x-auto px-4 pt-2.5 pb-3">
          {SCOPES.map((s) => {
            const on = s.id === scope;
            return (
              <button
                key={s.id}
                type="button"
                onClick={() => setScope(s.id)}
                aria-pressed={on}
                className={cn(
                  'h-[30px] flex-none rounded-full px-3.5 text-[12.5px] font-semibold outline-none',
                  'transition-[background-color,color] duration-[160ms] ease-[var(--ease-out)] focus-visible:shadow-[0_0_0_2px_var(--accent)]',
                  on ? 'bg-accent text-accent-fg' : 'bg-surface-2 text-dim hover:bg-surface-3 hover:text-ink'
                )}
              >
                {s.label}
              </button>
            );
          })}
        </div>

        <div className="h-px flex-none bg-line-soft" />

        {/* ---- results ---- */}
        <div ref={listRef} className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-1.5">
          {!typed && recent.length ? (
            <>
              <div className="flex items-center justify-between px-3 pt-2 pb-1">
                <span className="text-[10px] font-bold tracking-[.08em] text-ghost uppercase">Recent</span>
                <button
                  type="button"
                  onClick={() => { try { localStorage.removeItem(RECENT_KEY); } catch { /* nothing to clear */ } setRecent([]); }}
                  className="text-[11px] font-semibold text-mute transition-colors hover:text-ink"
                >
                  Clear
                </button>
              </div>
              <div className="flex flex-wrap gap-1.5 px-3 pb-2">
                {recent.map((r) => (
                  <button
                    key={r}
                    type="button"
                    onClick={() => { setTyped(r); inputRef.current?.focus(); }}
                    className="h-[28px] rounded-full bg-surface-2 px-3 text-[12px] font-semibold text-dim transition-colors hover:bg-surface-3 hover:text-ink"
                  >
                    {r}
                  </button>
                ))}
              </div>
            </>
          ) : null}

          {empty ? (
            <div className="flex flex-col items-center gap-2 px-3 py-12 text-center">
              <span className="grid h-12 w-12 place-items-center rounded-full bg-surface-2 text-mute">
                <Icons.Search size={20} />
              </span>
              <p className="m-0 text-[14px] font-semibold text-ink">
                {searching ? 'Looking…' : 'Nothing matches “' + typed.trim() + '”'}
              </p>
              {!searching ? (
                <p className="m-0 text-[12.5px] text-mute">
                  {scope === 'all' ? 'Try a username, a party name, or a crew tag.' : 'Nothing in ' + SCOPES.find((s) => s.id === scope)!.label + '. Press Tab to look everywhere.'}
                </p>
              ) : null}
            </div>
          ) : (
            shown.map((item, i) => {
              const head = item.group !== lastGroup ? item.group : null;
              lastGroup = item.group;
              const Glyph = item.icon;
              const on = i === cursor;
              return (
                <div key={item.id}>
                  {head ? (
                    <div className="px-3 pt-2.5 pb-1 text-[10px] font-bold tracking-[.08em] text-ghost uppercase">{head}</div>
                  ) : null}
                  <button
                    type="button"
                    data-active={on ? '1' : '0'}
                    onMouseMove={() => setActiveId(item.id)}
                    onClick={() => pick(item)}
                    className={cn(
                      'flex w-full items-center gap-3 rounded-[calc(12px*var(--radius-mul,1))] px-3 py-2 text-left',
                      'transition-colors duration-[120ms]',
                      on ? 'bg-accent-soft' : 'hover:bg-surface-2'
                    )}
                  >
                    <span
                      className={cn(
                        'grid h-9 w-9 flex-none place-items-center overflow-hidden rounded-[calc(10px*var(--radius-mul,1))]',
                        item.art ? '' : 'bg-surface-3'
                      )}
                      style={!item.art && item.color
                        ? { color: item.color, background: `color-mix(in srgb, ${item.color} 16%, transparent)` }
                        : undefined}
                    >
                      {item.art ?? (Glyph ? <Icons.Grid size={0} className="hidden" /> : null)}
                      {!item.art && Glyph ? <Glyph size={17} className={item.danger ? 'text-bad' : undefined} /> : null}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className={cn('block truncate text-[14px] font-semibold', item.danger ? 'text-bad' : on ? 'text-accent' : 'text-ink')}>
                        {item.title}
                      </span>
                      {item.sub ? <span className="block truncate text-[12px] text-mute">{item.sub}</span> : null}
                    </span>
                    {item.keys ? <Kbd>{item.keys}</Kbd> : null}
                    {on ? (
                      <span aria-hidden className="flex-none text-[11px] font-semibold text-accent">Enter ↵</span>
                    ) : null}
                  </button>
                </div>
              );
            })
          )}
        </div>

        <div className="flex flex-none items-center gap-4 border-t border-line-soft px-4 py-2 text-[11.5px] text-mute">
          <span className="flex items-center gap-1.5"><Kbd>↑ ↓</Kbd> move</span>
          <span className="flex items-center gap-1.5"><Kbd>tab</Kbd> filter</span>
          <span className="flex items-center gap-1.5"><Kbd>enter</Kbd> open</span>
          <span className="ml-auto">{shown.length ? shown.length + (shown.length === 1 ? ' result' : ' results') : ''}</span>
        </div>
      </div>
    </div>
  );
}
